import { useState } from 'react';
import { isInspectionLocked, type V2Inspection } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { captureCoverPhotoAction, removeCoverPhotoAction, useCoverPhotoUrl, type CoverLane } from '../../state/coverPhoto';
import { ActionSheet, ConfirmSheet, Icon, showToast, type ActionSheetItem } from '../../ui/components';
import { CoverPhotoViewer } from './CoverPhotoViewer';

// The cover-photo tile on the inspection card (Owner 2026-09-14: the red box on the 验屋 card). Without a
// cover it is the capture entry (拍封面照); with one it shows the photo and opens the options — view,
// take / choose another, remove. Once the final report exists the photo can only be viewed: the printed
// cover and the list thumbnail must stay the same photo.
export function CoverPhotoTile(props: { inspection: V2Inspection; testId?: string }) {
  const { t } = useT();
  const url = useCoverPhotoUrl(props.inspection, 'thumbnail');
  const has = props.inspection.coverPhoto !== null;
  const locked = isInspectionLocked(props.inspection);
  const [sheet, setSheet] = useState(false);
  const [viewer, setViewer] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  async function pick(lane: CoverLane) {
    setSheet(false);
    setBusy(true);
    try {
      const outcome = await captureCoverPhotoAction(props.inspection.id, lane);
      if (outcome.kind === 'error') showToast(t('cover.saveFailed', { message: outcome.message }), 'info');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setConfirm(false);
    try {
      await removeCoverPhotoAction(props.inspection.id);
    } catch (raw) {
      showToast(t('cover.saveFailed', { message: raw instanceof Error ? raw.message : String(raw) }), 'info');
    }
  }

  const items: ActionSheetItem[] = [
    ...(has
      ? [
          {
            key: 'view',
            label: t('cover.view'),
            icon: 'open_in_full' as const,
            onSelect: () => {
              setSheet(false);
              setViewer(true);
            },
            testId: 'cover-view'
          }
        ]
      : []),
    ...(!locked
      ? [
          { key: 'camera', label: t('photo.takePhoto'), icon: 'photo_camera' as const, onSelect: () => void pick('camera'), testId: 'cover-camera' },
          { key: 'library', label: t('photo.chooseLibrary'), icon: 'photo_library' as const, onSelect: () => void pick('library'), testId: 'cover-library' },
          ...(has
            ? [
                {
                  key: 'remove',
                  label: t('cover.remove'),
                  icon: 'delete' as const,
                  tone: 'danger' as const,
                  onSelect: () => {
                    setSheet(false);
                    setConfirm(true);
                  },
                  testId: 'cover-remove'
                }
              ]
            : [])
        ]
      : [])
  ];

  return (
    <>
      <button
        type="button"
        className={`cover-tile ${has ? 'cover-tile-has' : ''}`}
        onClick={() => setSheet(true)}
        disabled={busy || items.length === 0}
        aria-label={has ? t('cover.options') : t('cover.add')}
        data-testid={props.testId ?? 'cover-tile'}
        data-has-cover={has ? 'true' : 'false'}
      >
        {has ? (
          <>
            {url ? <img src={url} alt="" /> : <Icon name="image" />}
            <span className="cover-tile-badge" aria-hidden="true">
              <Icon name="photo_camera" />
            </span>
          </>
        ) : (
          <>
            <Icon name="add_a_photo" />
            <span>{t('cover.add')}</span>
          </>
        )}
      </button>
      {sheet ? <ActionSheet title={t('cover.options')} subtitle={locked ? t('cover.locked') : t('cover.fieldBody')} items={items} onClose={() => setSheet(false)} testId="cover-sheet" /> : null}
      {viewer ? <CoverPhotoViewer inspection={props.inspection} onClose={() => setViewer(false)} /> : null}
      {confirm ? (
        <ConfirmSheet title={t('cover.removeTitle')} body={t('cover.removeBody')} confirmLabel={t('cover.remove')} danger onConfirm={() => void remove()} onCancel={() => setConfirm(false)} testId="cover-remove-confirm" />
      ) : null}
    </>
  );
}

// The small thumbnail in front of a Reports row: tapping it opens the viewer, never the row.
export function CoverThumb(props: { inspection: V2Inspection; onOpen: () => void; testId?: string }) {
  const { t } = useT();
  const url = useCoverPhotoUrl(props.inspection, 'thumbnail');
  return (
    <button type="button" className="list-thumb" onClick={props.onOpen} aria-label={t('cover.view')} data-testid={props.testId ?? 'cover-thumb'}>
      {url ? <img src={url} alt="" /> : <Icon name="image" />}
    </button>
  );
}
